"use client"

/**
 * Floating preferences button component
 * Positioned in the bottom-left corner with a gear icon
 * Opens a dialog to change unit preferences (metric/imperial, currency, temperature)
 */
import { useState } from "react"
import { Settings, Ruler, Globe, Thermometer, RotateCcw, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { usePreferences } from "@/contexts/preferences-context"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { cn } from "@/lib/utils"
import type { UnitSystem, Currency, TemperatureUnit } from "@/lib/unit-conversions"

/**
 * Floating preferences button component
 * Positioned in the bottom-left corner with a gear icon
 * Opens a dialog to change unit preferences (metric/imperial, currency, temperature)
 */
export function PreferencesButton() {
  const [open, setOpen] = useState(false)
  const { preferences, setUnitSystem, setCurrency, setTemperatureUnit, resetToAutoDetect } = usePreferences()
  const { isMobile } = usePlatform()

  const handleOpen = () => {
    triggerHaptic('impact')
    setOpen(true)
  }

  const handleUnitChange = (val: string) => {
    triggerHaptic('selection')
    setUnitSystem(val as UnitSystem)
  }

  const handleCurrencyChange = (val: string) => {
    triggerHaptic('selection')
    setCurrency(val as Currency)
  }

  const handleTempChange = (val: string) => {
    triggerHaptic('selection')
    setTemperatureUnit(val as TemperatureUnit)
  }

  return (
    <>
      {/* Floating button in bottom-left corner - Hidden on mobile entirely */}
      <button
        onClick={handleOpen}
        className={cn(
          "fixed bottom-6 left-6 z-50 h-14 w-14 rounded-2xl flex items-center justify-center transition-all group",
          "bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border border-white/20 dark:border-slate-800",
          "shadow-2xl shadow-blue-900/10 hover:scale-110 active:scale-95",
          "hidden sm:flex"
        )}
        aria-label="Open preferences"
      >
        <div className="relative">
          <Settings className="h-6 w-6 text-slate-600 dark:text-slate-400 group-hover:rotate-90 transition-transform duration-500" />
          <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white dark:border-slate-900 shadow-sm animate-pulse" />
        </div>
      </button>

      {/* Preferences dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden bg-white/95 dark:bg-slate-900/95 backdrop-blur-2xl border-none rounded-[3rem] shadow-2xl">
          <div className="relative">
            {/* Header with Background Gradient */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 -ml-8 -mb-8 w-24 h-24 bg-blue-400/20 rounded-full blur-2xl" />
              
              <DialogHeader className="relative z-10 space-y-2">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center mb-2 rotate-3">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <DialogTitle className="text-3xl font-black uppercase tracking-tight italic text-white">Display Settings</DialogTitle>
                <DialogDescription className="text-blue-100 font-medium italic">
                  Personalize how spots, forecasts and currency appear across the platform.
                </DialogDescription>
              </DialogHeader>
            </div>

            <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto">
              {/* Unit System */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Ruler className="w-4 h-4 text-blue-500" />
                  <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Measurement Units</Label>
                </div>
                <RadioGroup
                  value={preferences.unitSystem}
                  onValueChange={handleUnitChange}
                  className="grid grid-cols-2 gap-3"
                >
                  {['metric', 'imperial'].map((unit) => (
                    <div key={unit} className="relative">
                      <RadioGroupItem value={unit} id={unit} className="peer sr-only" />
                      <Label
                        htmlFor={unit}
                        className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50/50 dark:peer-data-[state=checked]:bg-blue-900/20 transition-all cursor-pointer"
                      >
                        <span className="text-xs font-black uppercase tracking-widest">{unit}</span>
                        <span className="text-[10px] text-slate-500 font-medium mt-1">
                          {unit === 'metric' ? 'km, m, km/h' : 'mi, ft, mph'}
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Currency */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="w-4 h-4 text-green-500" />
                  <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Preferred Currency</Label>
                </div>
                <RadioGroup
                  value={preferences.currency}
                  onValueChange={handleCurrencyChange}
                  className="grid grid-cols-3 gap-2"
                >
                  {['USD', 'EUR', 'GBP'].map((curr) => (
                    <div key={curr} className="relative">
                      <RadioGroupItem value={curr} id={curr} className="peer sr-only" />
                      <Label
                        htmlFor={curr}
                        className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50/50 dark:peer-data-[state=checked]:bg-blue-900/20 transition-all cursor-pointer"
                      >
                        <span className="text-xs font-black uppercase tracking-widest">{curr}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Temperature Unit */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-1">
                  <Thermometer className="w-4 h-4 text-orange-500" />
                  <Label className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Temperature Scale</Label>
                </div>
                <RadioGroup
                  value={preferences.temperatureUnit}
                  onValueChange={handleTempChange}
                  className="grid grid-cols-2 gap-3"
                >
                  {['celsius', 'fahrenheit'].map((temp) => (
                    <div key={temp} className="relative">
                      <RadioGroupItem value={temp} id={temp} className="peer sr-only" />
                      <Label
                        htmlFor={temp}
                        className="flex flex-col items-center justify-center p-4 rounded-2xl border-2 border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50/50 dark:peer-data-[state=checked]:bg-blue-900/20 transition-all cursor-pointer"
                      >
                        <span className="text-xs font-black uppercase tracking-widest">{temp}</span>
                        <span className="text-[10px] text-slate-500 font-medium mt-1">
                          {temp === 'celsius' ? '°C' : '°F'}
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Reset to Auto-detect */}
              <div className="pt-4 flex flex-col gap-3">
                <Button
                  variant="outline"
                  onClick={() => { triggerHaptic('impact'); resetToAutoDetect(); }}
                  className="w-full h-14 rounded-2xl border-slate-100 dark:border-slate-800 font-black uppercase tracking-widest text-[10px] italic flex items-center justify-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-800 group transition-all"
                >
                  <RotateCcw className="w-3 h-3 text-slate-400 group-hover:rotate-[-180deg] transition-transform duration-500" />
                  Reset to Auto-detect
                </Button>
                
                <Button
                  onClick={() => setOpen(false)}
                  className="w-full h-14 bg-slate-950 dark:bg-white text-white dark:text-slate-950 rounded-2xl font-black uppercase tracking-widest text-xs active:scale-95 transition-all"
                >
                  Done
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
